import { type ReactNode, useEffect, useRef, useState } from "react";
import { cx } from "../../lib/cx";
import { type MediaClip, Plate } from "./media";
import { cutoutUrl } from "./urls";

export interface ExerciseThumbProps {
  /** `""` for a row that names the exercise beside it, as on `Media`. */
  alt: string;
  /** Sizes the box, whichever of the three it draws. */
  className?: string;
  clip: MediaClip | null;
  /** The exercise's cut-out (`D196`); absent or `null` keeps the poster. */
  cutoutHash: string | null | undefined;
  /** What to draw with neither a cut-out nor a clip: the row's own glyph. */
  fallback: ReactNode;
  /** The poster plate's inset, as on `Plate`. */
  pad?: string;
}

/**
 * A list row's picture of an exercise (`D199`): its CUT-OUT where there is
 * one, else its poster on the white plate, else the row's glyph.
 *
 * ── WHY THE CUT-OUT FIRST ──────────────────────────────────────────────────
 * The poster is drawn on white, so in a row it is a white tile in both
 * themes — a bright hole in the dark one (`plate`, `docs/DESIGN.md` §2). The
 * cut-out is the same figure on a transparent ground: it sits on the row's
 * own well (`surface-2`) in either theme, and a list of them reads as a list
 * of movements rather than a column of white squares.
 *
 * ── STILL A STILL ──────────────────────────────────────────────────────────
 * A plain lazy `<img>`, and never a `<video>`: a row is a picture (`D08`,
 * `D162`). The cut-out is content-addressed and immutable like the poster
 * (`ui/media/urls.ts`), and one that fails — offline, or an object the CDN
 * does not have — falls back to the poster rather than a broken-image glyph.
 */
export function ExerciseThumb({
  alt,
  className,
  clip,
  cutoutHash,
  fallback,
  pad,
}: ExerciseThumbProps) {
  if (cutoutHash) {
    return (
      <Cutout
        alt={alt}
        className={className}
        clip={clip}
        fallback={fallback}
        hash={cutoutHash}
        key={cutoutHash}
        pad={pad}
      />
    );
  }
  return (
    <Poster
      alt={alt}
      className={className}
      clip={clip}
      fallback={fallback}
      pad={pad}
    />
  );
}

function Poster({
  alt,
  className,
  clip,
  fallback,
  pad,
}: Omit<ExerciseThumbProps, "cutoutHash">) {
  if (clip === null) {
    return <>{fallback}</>;
  }
  return <Plate alt={alt} className={className} clip={clip} pad={pad} />;
}

function Cutout({
  alt,
  className,
  clip,
  fallback,
  hash,
  pad,
}: Omit<ExerciseThumbProps, "cutoutHash"> & { hash: string }) {
  const img = useRef<HTMLImageElement | null>(null);
  const [failed, setFailed] = useState(false);

  // A listener, not an `onError` prop: Biome refuses a handler on an `<img>`
  // (`media.tsx` says the same). `complete` with no pixels is a load that
  // already failed before this effect ran.
  useEffect(() => {
    const element = img.current;
    if (element === null) {
      return;
    }
    const fail = () => setFailed(true);
    element.addEventListener("error", fail);
    if (element.complete && element.naturalWidth === 0 && element.src) {
      setFailed(true);
    }
    return () => element.removeEventListener("error", fail);
  }, []);

  if (failed) {
    return (
      <Poster
        alt={alt}
        className={className}
        clip={clip}
        fallback={fallback}
        pad={pad}
      />
    );
  }
  return (
    <span
      className={cx(
        "relative block shrink-0 overflow-hidden bg-surface-2",
        className
      )}
      data-cutout=""
    >
      <img
        alt={alt}
        className="absolute inset-0 block size-full object-contain p-0.5"
        decoding="async"
        draggable={false}
        // The cut-outs are encoded square, 340 px; the classes size the box.
        height={340}
        loading="lazy"
        ref={img}
        src={cutoutUrl(hash)}
        width={340}
      />
    </span>
  );
}
